import React, { useState, ChangeEvent, FormEvent, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { BaseURL } from "../../../components/utils/url";
import { slideFromRight } from "../../../animations/Animations";
import Logo from "../../../assets/Logo.svg";

interface FormData {
  email: string;
  password: string;
}

interface UserData {
  id: number;
  name: string;
  role: string;
}

const Login: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    email: "",
    password: "",
  });
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    localStorage.clear();
  }, []);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Clear error message when user starts typing
    if (error) {
      setError("");
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value.trim(),
    }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    if (!formData.email || !formData.password) {
      setError("Please fill in all fields");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);

    try {
      const response = await BaseURL.post("super-admin/login", {
        email: formData.email,
        password: formData.password,
      });

      // Handle user data storage
      const userData: UserData = response.data.data.data;
      if (userData) {
        // Store user data
        localStorage.setItem("userData", JSON.stringify(userData));
        // Store token if present
        if (response.data.data.token) {
          localStorage.setItem("authToken", response.data.data.token);
          navigate("/administrator");
        }
      }
    } catch (err: any) {
      // Check if the error is due to invalid credentials (401)
      if (err.response && err.response.status === 401) {
        setError("Invalid email or password");
      } else {
        setError("Invalid email or password");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Login Form Section */}
          <motion.div
            className="w-full max-w-md mx-auto"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="relative">
              <motion.div
                className="absolute inset-0 bg-blue-500 shadow-lg transform -skew-y-6 sm:skew-y-0 sm:-rotate-6 sm:rounded-3xl"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              ></motion.div>
              <motion.div
                className="relative bg-white shadow-lg sm:rounded-3xl px-4 py-10 sm:p-20"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <div>
                  <h1 className="text-2xl font-semibold">Login</h1>
                </div>
                <div className="divide-y divide-gray-200">
                  <form
                    onSubmit={handleSubmit}
                    className="py-8 text-base leading-6 space-y-4 text-gray-700 sm:text-lg sm:leading-7"
                  >
                    <AnimatePresence mode="wait">
                      {error && (
                        <motion.div
                          className="text-red-500 text-sm"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          {error}
                        </motion.div>
                      )}
                    </AnimatePresence>
                    <div className="relative">
                      <input
                        autoComplete="email"
                        id="email"
                        name="email"
                        type="email"
                        className="peer placeholder-transparent h-10 w-full border-b-2 border-gray-300 text-gray-900 focus:outline-none focus:borer-rose-600"
                        placeholder="Email"
                        value={formData.email}
                        onChange={handleChange}
                      />
                      <label
                        htmlFor="email"
                        className="absolute left-0 -top-3.5 text-gray-600 text-sm peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-440 peer-placeholder-shown:top-2 transition-all peer-focus:-top-3.5 peer-focus:text-gray-600 peer-focus:text-sm"
                      >
                        Email
                      </label>
                    </div>
                    <div className="relative">
                      <input
                        autoComplete="current-password"
                        id="password"
                        name="password"
                        type="password"
                        className="peer placeholder-transparent h-10 w-full border-b-2 border-gray-300 text-gray-900 focus:outline-none focus:borer-rose-600"
                        placeholder="Password"
                        value={formData.password}
                        onChange={handleChange}
                      />
                      <label
                        htmlFor="password"
                        className="absolute left-0 -top-3.5 text-gray-600 text-sm peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-440 peer-placeholder-shown:top-2 transition-all peer-focus:-top-3.5 peer-focus:text-gray-600 peer-focus:text-sm"
                      >
                        Password
                      </label>
                    </div>
                    <div className="relative">
                      <button
                        type="submit"
                        className="bg-black text-white rounded-md px-4 py-2 w-full sm:w-auto disabled:opacity-50"
                        disabled={loading}
                      >
                        {loading ? "Loading..." : "Submit"}
                      </button>
                    </div>
                    <div className="text-sm text-right">
                      <button
                        type="button"
                        onClick={() =>
                          navigate("/administrator/forgot-password")
                        }
                        className="text-blue-500 hover:text-blue-700"
                      >
                        Forgot Password?
                      </button>
                    </div>
                  </form>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Logo Section */}
          <motion.div
            className="flex justify-center items-center order-first lg:order-last"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <motion.img
              src={Logo}
              alt="syncai logo"
              initial="hidden"
              animate="visible"
              variants={slideFromRight}
            />
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Login;
